/**
 * Eraya price-test variant sync (store-specific)
 *
 * This theme rewrites the product price on every variant change
 * (VariantRadios / VariantRadiosDetail in theme.js -> updatePrice/renderProductInfor),
 * which wipes the test price injected by the Eraya SDK. The theme does not emit a
 * custom variant event, but it DOES dispatch a bubbling `change` on input[name="id"]
 * just before it rewrites the price DOM.
 *
 * We listen for that change, keep the product root's data-variant-id in sync, then
 * re-apply the Eraya test price for the newly selected variant AFTER the theme has
 * finished its own price rewrite.
 */
(function () {
  'use strict';

  function getManager() {
    var E = window.Eraya;
    if (E && E.sdk && typeof E.sdk.getPriceTestManager === 'function') {
      try {
        return E.sdk.getPriceTestManager();
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  function reapply(root, variantId) {
    if (!root || !variantId) return;
    var productId = root.getAttribute('data-product-id');
    if (!productId) return;

    // Keep the root attribute fresh so the engine's container/variant match passes
    // for variant-level price tests.
    root.setAttribute('data-variant-id', variantId);

    var mgr = getManager();
    if (!mgr || typeof mgr.updatePricesForProduct !== 'function') return;

    try {
      // If this variant is in a test -> injects the test price.
      // If it is not -> no-op, leaving the theme's real price in place.
      mgr.updatePricesForProduct(String(productId), String(variantId));
    } catch (e) {
      /* swallow - never break the storefront */
    }
  }

  function onVariantChange(input) {
    var variantId = input && input.value;
    if (!variantId) return;

    // The product form lives inside the product root that carries data-product-id
    // (added in product-single-layout.liquid / main-product.liquid).
    var root = input.closest('[data-product-id]');
    if (!root) {
      root = document.querySelector(
        '.product-detail__information[data-product-id], .product-single__information[data-product-id]'
      );
    }
    if (!root) return;

    // Primary pass: a microtask runs after the theme's synchronous
    // updatePrice/renderProductInfor (same call stack, right after this change
    // event is dispatched) but BEFORE the browser paints -> no price flash.
    var run = function () {
      reapply(root, variantId);
    };
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
    } else {
      Promise.resolve().then(run);
    }

    // Guard pass: catches any later/async re-render the theme does
    // (badges, sticky add-to-cart mirror, media swap animations).
    setTimeout(run, 150);
  }

  // Delegated, capture-phase listener so it works regardless of where the form sits.
  document.addEventListener(
    'change',
    function (e) {
      var t = e.target;
      if (t && t.name === 'id' && t.tagName === 'INPUT') {
        onVariantChange(t);
      }
    },
    true
  );

  /* ------------------------------------------------------------------ *
   * AJAX-injected product cards (predictive search, quickview, etc.)
   *
   * The Eraya SDK only scans the DOM that exists at page load. This theme
   * injects product cards LATER via innerHTML — e.g. predictive search
   * (BlsSearchShopify.renderSearchResults -> [data-predictive-search]) —
   * so those cards keep their original price. We watch for newly added
   * cards (any element with data-product-id) and ask the SDK to re-apply
   * test prices for the products now on the page.
   * ------------------------------------------------------------------ */
  var rescanTimer = null;

  function rescanInjectedCards() {
    var mgr = getManager();
    if (!mgr) return;
    try {
      if (typeof mgr.updatePricesOnCurrentPage === 'function') {
        mgr.updatePricesOnCurrentPage();
      } else if (typeof mgr.rescanPageForPriceUpdates === 'function') {
        mgr.rescanPageForPriceUpdates();
      }
    } catch (e) {
      /* swallow - never break the storefront */
    }
  }

  function scheduleRescan() {
    if (rescanTimer) clearTimeout(rescanTimer);
    // Debounce: search fires many rapid renders while typing.
    rescanTimer = setTimeout(rescanInjectedCards, 120);
  }

  // Returns true if the node is, or contains, a product card the SDK can target.
  function containsProductCard(node) {
    if (node.nodeType !== 1) return false; // elements only
    if (node.hasAttribute && node.hasAttribute('data-product-id')) return true;
    return (
      typeof node.querySelector === 'function' &&
      node.querySelector('[data-product-id]') !== null
    );
  }

  if (typeof MutationObserver !== 'undefined') {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (containsProductCard(added[j])) {
            scheduleRescan();
            return; // one schedule per batch is enough
          }
        }
      }
    });

    var start = function () {
      // Only react to ADDED nodes (childList). The SDK's own price updates
      // change text/attributes, which we ignore — so there is no feedback loop.
      observer.observe(document.body, { childList: true, subtree: true });
    };

    if (document.body) {
      start();
    } else {
      document.addEventListener('DOMContentLoaded', start);
    }
  }
})();
